import type { CSSProperties } from 'react';
import { Checkbox, Menu, Tooltip } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';

import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import type { ListItem, SecondaryFilterOption } from '../../ControlTower/types';
import TeamGroupOptions from './TeamGroupOptions';
import '../Filters.css';

type SubFilterOptionsListProps = {
  filterBy: string;
  options: SecondaryFilterOption[];
  selectedOptions: SecondaryFilterOption[];
  onCheckboxChange: (
    event: CheckboxChangeEvent,
    option: SecondaryFilterOption
  ) => void;
  onApplyPreset?: (
    presetIds: { id: string | number }[],
    appendMode: boolean
  ) => void;
  onDeletePreset?: (presetId: string) => void;
  setSelectedOptions?: (options: ListItem[]) => void;
  /**
   * Team filter: every team option before search narrows `options`, so a
   * group selects all its teams in the picker. Defaults to `options`.
   */
  allOptions?: SecondaryFilterOption[];
  searchQuery?: string;
};

const isTeamFilter = (filterBy: string) =>
  filterBy === 'team' || filterBy === 'teams';

const getOptionLabel = (option: SecondaryFilterOption) =>
  option?.title || option?.name || option?.courier_name || String(option.id);

const isOptionDisabled = (option: SecondaryFilterOption) =>
  option.enabled === false || option.disabled === true;

const optionRowStyle: CSSProperties = {
  padding: '0 6px',
  lineHeight: '20px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
};

const SubFilterOptionsList: React.FC<SubFilterOptionsListProps> = ({
  filterBy,
  options,
  selectedOptions,
  onCheckboxChange,
  onApplyPreset,
  onDeletePreset,
  setSelectedOptions,
  allOptions,
  searchQuery,
}) => {
  const showSelectAll =
    isTeamFilter(filterBy) && !!setSelectedOptions && options.length > 0;

  const selectableOptions = options.filter(
    (option) => !isOptionDisabled(option)
  );

  const selectedIds = new Set(
    selectedOptions.map((option) => String(option.id))
  );

  const allVisibleSelected =
    selectableOptions.length > 0 &&
    selectableOptions.every((option) => selectedIds.has(String(option.id)));

  const handleSelectAll = (checked: boolean) => {
    if (!setSelectedOptions) return;

    if (checked) {
      const merged = [...selectedOptions];
      const existingIds = new Set(merged.map((option) => String(option.id)));

      selectableOptions.forEach((option) => {
        if (!existingIds.has(String(option.id))) {
          merged.push({
            id: String(option.id),
            name: getOptionLabel(option),
          });
        }
      });

      setSelectedOptions(merged);
      return;
    }

    const visibleIds = new Set(
      selectableOptions.map((option) => String(option.id))
    );
    setSelectedOptions(
      selectedOptions.filter((option) => !visibleIds.has(String(option.id)))
    );
  };

  return (
    <>
      {showSelectAll && (
        <Menu.Item key='__select_all' style={optionRowStyle}>
          <Checkbox
            className='custom-menu-shipment-checkbox-only checkbox-sm'
            checked={allVisibleSelected}
            onChange={(event) => handleSelectAll(event.target.checked)}
            style={{ fontSize: 12 }}
          >
            Select all
          </Checkbox>
        </Menu.Item>
      )}

      {isTeamFilter(filterBy) && setSelectedOptions && (
        <TeamGroupOptions
          teamOptions={allOptions ?? options}
          selectedOptions={selectedOptions}
          setSelectedOptions={setSelectedOptions}
          searchQuery={searchQuery}
          rowStyle={optionRowStyle}
        />
      )}

      {options.map((option) => (
        <Menu.Item key={option.id} style={optionRowStyle}>
          {filterBy === 'saved_filters' ? (
            <PresetRow
              option={option}
              onApplyPreset={onApplyPreset}
              onDeletePreset={onDeletePreset}
            />
          ) : (
            <CheckboxRow
              filterBy={filterBy}
              option={option}
              selectedOptions={selectedOptions}
              onCheckboxChange={onCheckboxChange}
            />
          )}
        </Menu.Item>
      ))}
    </>
  );
};

/** Tooltip lines with stable keys (a repeated line gets its own key). */
const keyedLines = (text: string) => {
  const seen = new Map<string, number>();
  return text.split('\n').map((line) => {
    const count = (seen.get(line) ?? 0) + 1;
    seen.set(line, count);
    return { key: `${line}#${count}`, line };
  });
};

const PresetRow: React.FC<{
  option: SecondaryFilterOption;
  onApplyPreset?: (
    presetIds: { id: string | number }[],
    appendMode: boolean
  ) => void;
  onDeletePreset?: (presetId: string) => void;
}> = ({ option, onApplyPreset, onDeletePreset }) => (
  <div className='subfilter-preset-row'>
    <Tooltip
      title={
        option?.filterCount ? (
          <div className='saved-filter-tooltip-content'>
            {keyedLines(option.tooltipText || '').map(({ key, line }) => (
              <span key={key}>{line}</span>
            ))}
          </div>
        ) : null
      }
      mouseEnterDelay={0.3}
      placement='right'
      overlayClassName='saved-filter-tooltip'
    >
      <span
        role='button'
        tabIndex={0}
        className='subfilter-preset-apply'
        onClick={(e) => {
          e.stopPropagation();
          onApplyPreset?.([{ id: option.id }], false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onApplyPreset?.([{ id: option.id }], false);
          }
        }}
      >
        <TruncateWithTooltip
          text={option?.title || option?.name}
          max={25}
          render={(t) => (
            <span className='subfilter-preset-name'>
              <span>{t}</span>
            </span>
          )}
        />

        <span className='subfilter-preset-count'>
          {option?.filterCount || 0} filters
        </span>
      </span>
    </Tooltip>

    {option?._isPreset && (
      <button
        className='preset-del-btn'
        onClick={(e) => {
          e.stopPropagation();
          onDeletePreset?.(String(option.id));
        }}
        aria-label='Delete preset'
        title='Delete preset'
      >
        x
      </button>
    )}
  </div>
);

const CheckboxRow: React.FC<{
  filterBy: string;
  option: SecondaryFilterOption;
  selectedOptions: SecondaryFilterOption[];
  onCheckboxChange: (
    event: CheckboxChangeEvent,
    option: SecondaryFilterOption
  ) => void;
}> = ({ filterBy, option, selectedOptions, onCheckboxChange }) => {
  const checked = selectedOptions.some(
    (selected) => String(selected.id) === String(option.id)
  );
  const isDisabled = option.enabled === false || option.disabled === true;

  return (
    <Tooltip
      title={isDisabled ? 'Not available for your role.' : ''}
      mouseEnterDelay={0.3}
    >
      <Checkbox
        className='custom-menu-shipment-checkbox-only checkbox-sm'
        checked={checked}
        disabled={isDisabled}
        onChange={(e) => onCheckboxChange(e, option)}
        style={{
          opacity: isDisabled ? 0.5 : 1,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        <TruncateWithTooltip
          text={option?.title || option?.name || option?.courier_name}
          max={25}
          render={(t: string) => (
            <span className='subfilter-option-label'>
              {t}
              {filterBy === 'temperature' && option?.name2 && (
                <span className='subfilter-option-secondary'>
                  {' '}
                  {option.name2}
                </span>
              )}
            </span>
          )}
        />
      </Checkbox>
    </Tooltip>
  );
};

export default SubFilterOptionsList;
